import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import { inputAdornmentClasses } from '@mui/material/InputAdornment';
import { SxProps, Theme } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers-pro';
import dayjs, { Dayjs } from 'dayjs';
import { isNil } from 'lodash-es';
import { useMemo } from 'react';
import { FieldValues, useController, UseControllerProps } from 'react-hook-form';

import { desktopActionBar } from '~components/FormFields/desktopActionBar';

import Label from './Label';

type Props<T extends FieldValues> = UseControllerProps<T> & {
  dataUi?: string;
  // Renders the field's clear (×) button so an optional date can be emptied
  // without keyboard-deleting the text.
  isClearable?: boolean;
  isDisabled?: boolean;
  isRequired?: boolean;
  label?: React.ReactNode;
  // Calendar bounds — used by paired From/To pickers to keep a range valid.
  maxDate?: Dayjs;
  minDate?: Dayjs;
  // Overrides the empty-field placeholder (defaults to the mm/dd/yyyy format
  // hint) — e.g. "Any date" on an optional, open-ended range bound.
  placeholder?: string;
  // Lifts the calendar popper above elevated ancestors (it defaults to
  // zIndex.modal, so it opens BEHIND a host layered above a dialog, e.g. a
  // drawer at modal + 1).
  popperSx?: SxProps<Theme>;
  suppressErrorMessage?: boolean;
  sx?: SxProps<Theme>;
};

export default function DateInput<T extends FieldValues>({
  control,
  dataUi,
  isClearable,
  isDisabled,
  isRequired,
  label,
  maxDate,
  minDate,
  name,
  placeholder,
  popperSx,
  suppressErrorMessage,
  sx,
}: Props<T>) {
  const { field, fieldState } = useController({ control, name });
  const error = fieldState.error?.message ?? fieldState.error?.root?.message;

  const value = useMemo(() => {
    if (isNil(field.value)) {
      return null;
    }

    try {
      const value = dayjs.tz(field.value);

      if (value.isValid()) {
        return value;
      }

      return null;
    } catch {
      return null;
    }
  }, [field.value]);

  return (
    <FormControl
      disabled={isDisabled}
      error={Boolean(error)}
      fullWidth
      sx={[
        { display: 'flex', flexDirection: 'column', gap: 0.25 },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {label && <Label isRequired={isRequired}>{label}</Label>}

      <DatePicker
        closeOnSelect={false}
        enableAccessibleFieldDOMStructure={false}
        disabled={isDisabled}
        // Re-anchored as the same CALENDAR DAY in the app's default timezone to
        // match `value` below — MUI compares days in the value's timezone, and
        // converting the instant instead (dayjs.tz(minDate)) shifts the bound
        // into the neighboring day whenever the timezones' offsets differ.
        //
        // isValid() is required, not defensive: paired From/To pickers bound each
        // other, so a half-typed date arrives here as an INVALID Dayjs, whose
        // .format() returns the string 'Invalid Date' — and dayjs.tz() throws
        // RangeError on that, taking down the page via the error boundary
        // (TRE-16113). An uninterpretable bound is dropped until the sibling parses.
        maxDate={maxDate?.isValid() ? dayjs.tz(maxDate.format('YYYY-MM-DD')) : undefined}
        minDate={minDate?.isValid() ? dayjs.tz(minDate.format('YYYY-MM-DD')) : undefined}
        onChange={field.onChange}
        value={value}
        slotProps={{
          actionBar: desktopActionBar([]),
          ...(isClearable ? { field: { clearable: true } } : {}),
          ...(popperSx ? { popper: { sx: popperSx } } : {}),
          textField: {
            className: isRequired ? 'required' : '',
            error: Boolean(error),
            fullWidth: true,
            placeholder: placeholder ?? 'mm/dd/yyyy',
            required: isRequired,
            slotProps: {
              htmlInput: {
                sx: { fontSize: '12px', height: '23px' },
                ...(dataUi && { 'data-ui': dataUi }),
              },
            },
            variant: 'outlined',
            sx: { ...sx, [`& .${inputAdornmentClasses.root}`]: { ml: 0 } },
          },
        }}
      />

      {error && !suppressErrorMessage && (
        <FormHelperText sx={{ m: 0, fontSize: '10px', fontWeight: 700 }}>
          {error}
        </FormHelperText>
      )}
    </FormControl>
  );
}
